// ChangeDate.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Renew.css";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

/** Helpers */
const pad2 = (n) => String(n).padStart(2, "0");

const API = "https://www.lianfoong.com/api";

const toDdMmYyyy = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  return `${pad2(dt.getDate())}${pad2(dt.getMonth() + 1)}${dt.getFullYear()}`;
};

const todayDdMmYyyy = () => toDdMmYyyy(new Date());

/** Convert ISO "yyyy-MM-dd" -> "ddMMyyyy" */
const isoToDdMmYyyy = (iso) => {
  if (!iso) return todayDdMmYyyy();
  const [y, m, d] = iso.split("-");
  return `${d}${m}${y}`;
};

/** Validate and build a real JS Date from "ddMMyyyy" */
const parseDdMmYyyy = (s) => {
  if (!/^\d{8}$/.test(s)) return null;
  const day = parseInt(s.slice(0, 2), 10);
  const month = parseInt(s.slice(2, 4), 10);
  const year = parseInt(s.slice(4, 8), 10);

  if (month < 1 || month > 12) return null;
  const dt = new Date(year, month - 1, day);
  // Cross-check: JS Date will auto-roll (e.g., 31 Nov -> 1 Dec),
  // so validate all parts match exactly.
  if (
    dt.getFullYear() !== year ||
    dt.getMonth() + 1 !== month ||
    dt.getDate() !== day
  ) {
    return null;
  }
  return dt;
};

const ChangeDate = ({ setAppDate }) => {
  useEscapeToGoBack();
  const navigate = useNavigate();

  const [inputDate, setInputDate] = useState(todayDdMmYyyy());
  const [loading, setLoading] = useState(true);

  // Prefill from backend (override date if exists; else today)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API}/system-date`);
        if (!res.ok) throw new Error("Failed to read system date");
        const data = await res.json(); // { date: "yyyy-MM-dd" | null }
        const ddmmyyyy = data?.date ? isoToDdMmYyyy(data.date) : todayDdMmYyyy();
        if (mounted) setInputDate(ddmmyyyy);
      } catch {
        // If backend not reachable, still allow local entry
        if (mounted) setInputDate(todayDdMmYyyy());
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    const parsed = parseDdMmYyyy(inputDate);
    if (!parsed) {
      alert("Please enter a valid date in DDMMYYYY format.");
      return;
    }

    try {
     const res = await fetch(`${API}/system-date`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: inputDate }), // backend accepts ddMMyyyy
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Failed to set system date");
      }

      // Optional: let the app know (if parent passed setAppDate)
      setAppDate?.(inputDate);

      alert("Date changed successfully!");
      navigate("/dashboard");
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleClear = async () => {
    try {
      const res = await fetch(`${API}/system-date`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to clear override");
      const t = todayDdMmYyyy();
      setInputDate(t);
      setAppDate?.(t);
      alert("Override cleared. Using real system date.");
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="renew-container">
        <h2 className="title">Change System Date</h2>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="renew-container">
      <h2 className="title">Change System Date</h2>

      <form className="form-group" onSubmit={handleSubmit}>
        <label>Enter New Date (DDMMYYYY):</label>
        <input
          type="text"
          value={inputDate}
          onChange={(e) => {
            // Allow only digits, max 8
            const v = e.target.value.replace(/\D/g, "").slice(0, 8);
            setInputDate(v);
          }}
          maxLength={8}
          placeholder="DDMMYYYY"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit(e);
          }}
        />
      </form>

      <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
        <button className="search-btn" onClick={handleSubmit}>
          Submit
        </button>
        <button
          type="button"
          className="search-btn"
          style={{ background: "#999" }}
          onClick={handleClear}
          title="Clear override and use real system date"
        >
          Clear Override
        </button>
      </div>
    </div>
  );
};

export default ChangeDate;
